import React from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { Skeleton } from '@/components/ui/skeleton';

const CLASS_COLORS: Record<string, string> = {
  forex: 'hsl(210, 100%, 56%)',
  crypto: 'hsl(265, 90%, 65%)',
  us_stock: 'hsl(142, 71%, 45%)',
  commodity: 'hsl(38, 92%, 50%)',
  bond: 'hsl(210, 15%, 55%)',
};

interface AllocationPieChartProps {
  allocationByClass: Record<string, number> | null | undefined;
  isLoading?: boolean;
}

export const AllocationPieChart: React.FC<AllocationPieChartProps> = ({ allocationByClass, isLoading }) => {
  if (isLoading) {
    return <Skeleton className="w-[250px] h-[250px] rounded-full mx-auto" />;
  }

  const parsed = allocationByClass && typeof allocationByClass === 'object'
    ? Object.entries(allocationByClass as Record<string, number>)
        .filter(([, v]) => v > 0)
        .map(([name, value]) => ({ name, value: Number(value) }))
    : [];

  const data = parsed.length > 0 ? parsed : [{ name: 'Cash', value: 100 }];
  const totalInvested = parsed.reduce((s, d) => s + d.value, 0);

  const renderCenterLabel = () => (
    <text x="50%" y="50%" textAnchor="middle" dominantBaseline="central" className="fill-foreground">
      <tspan x="50%" dy="-0.4em" className="text-sm font-semibold" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
        ${totalInvested.toLocaleString(undefined, { maximumFractionDigits: 0 })}
      </tspan>
      <tspan x="50%" dy="1.4em" className="text-xs fill-muted-foreground">
        Invested
      </tspan>
    </text>
  );

  return (
    <div className="w-[250px] h-[250px] mx-auto">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            innerRadius={65}
            outerRadius={100}
            dataKey="value"
            isAnimationActive={true}
            stroke="none"
          >
            {data.map((entry) => (
              <Cell
                key={entry.name}
                fill={CLASS_COLORS[entry.name] ?? 'hsl(210, 15%, 55%)'}
              />
            ))}
          </Pie>
          <Tooltip
            formatter={(value: number, name: string) => {
              const total = data.reduce((s, d) => s + d.value, 0);
              const pct = total > 0 ? ((value / total) * 100).toFixed(1) : '0';
              return [`$${value.toLocaleString()} (${pct}%)`, name.replace('_', ' ')];
            }}
            contentStyle={{
              backgroundColor: 'hsl(220, 15%, 10%)',
              border: '1px solid hsl(220, 15%, 15%)',
              borderRadius: '8px',
              color: 'hsl(210, 20%, 92%)',
              fontSize: '12px',
              fontFamily: "'JetBrains Mono', monospace",
            }}
          />
          {renderCenterLabel()}
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
};
